import { useCallback, useEffect, useRef, useState, type CSSProperties, type FormEvent } from "react";
import type { AdminCommercialContractResponseDto } from "@madamuh/domain";
import { useNavigate, useParams } from "react-router-dom";
import {
  fetchAdminCommercialContract,
  requestAdminContractEmailChange
} from "../../shared/api/commercialContract";
import { getEnv } from "../../shared/lib/env";
import {
  getProtectedApiErrorMessage,
  isUnauthenticatedApiError
} from "../../shared/session/protectedApiError";
import { useProtectedRouteSession } from "../../shared/session/useProtectedRouteSession";
import { OperatorPageScaffold } from "../../shared/ui/protected/OperatorPageScaffold";

const pageLinks = [
  { to: "/t/:tenantSlug/teacher/admin", label: "管理ホーム" },
  { to: "/t/:tenantSlug/teacher/admin/billing", label: "請求" },
  { to: "/t/:tenantSlug/teacher/admin/policies", label: "規約・ポリシー" }
];

const cardStyle = {
  display: "grid",
  gap: 12,
  padding: 16,
  borderRadius: 14,
  border: "1px solid #e5e7eb",
  background: "#fff"
} satisfies CSSProperties;

const mutedStyle = { margin: 0, fontSize: 13, color: "#6b7280" } satisfies CSSProperties;

function valueOrDash(value: string | number | null | undefined): string {
  return value === null || value === undefined || value === "" ? "-" : String(value);
}

function formatDate(value: string | null | undefined): string {
  if (!value) {
    return "-";
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "-";
  }

  const parts = new Intl.DateTimeFormat("ja-JP", {
    timeZone: "Asia/Tokyo",
    year: "numeric",
    month: "numeric",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hourCycle: "h23"
  }).formatToParts(date);
  const year = parts.find((part) => part.type === "year")?.value;
  const month = parts.find((part) => part.type === "month")?.value;
  const day = parts.find((part) => part.type === "day")?.value;
  const hour = parts.find((part) => part.type === "hour")?.value;
  const minute = parts.find((part) => part.type === "minute")?.value;

  return year && month && day && hour && minute
    ? `${year}年${month}月${day}日 ${hour}時${minute}分`
    : "-";
}

function describeContractStatus(value: AdminCommercialContractResponseDto["contractStatus"]): string {
  switch (value) {
    case "trialing":
      return "無料期間中";
    case "active":
      return "契約中";
    case "cancellationScheduled":
      return "解約予約中";
    case "ended":
      return "契約終了";
    case null:
    default:
      return "未設定";
  }
}

function describeRestrictionStatus(value: AdminCommercialContractResponseDto["serviceRestrictionStatus"]): string {
  switch (value) {
    case "none":
      return "制限なし";
    case "paymentRestricted":
      return "支払い確認中";
    case "riskSuspended":
      return "リスク確認により停止中";
    case "operatorSuspended":
      return "運営により停止中";
    case null:
    default:
      return "未設定";
  }
}

function describePricingMode(value: AdminCommercialContractResponseDto["contractPricingMode"]): string {
  switch (value) {
    case "paymentProcessing":
      return "MADAMUH決済利用型";
    case "management":
      return "管理利用型";
    case null:
    default:
      return "未設定";
  }
}

function describeContactStatus(value: AdminCommercialContractResponseDto["contractContact"]["status"]): string {
  switch (value) {
    case "verified":
      return "確認済み";
    case "changePending":
      return "変更手続き中";
    case "pending":
      return "未確認";
    case null:
    default:
      return "未設定";
  }
}

function describePaymentStatus(
  value: AdminCommercialContractResponseDto["platformBillingPaymentProfile"]["status"]
): string {
  switch (value) {
    case "active":
      return "登録済み";
    case "setupRequired":
      return "登録が必要";
    case "expired":
      return "有効期限切れ";
    case "failed":
      return "確認が必要";
    case "missing":
    default:
      return "未登録";
  }
}

function describePendingStage(
  value: NonNullable<AdminCommercialContractResponseDto["pendingContractEmailChange"]>["status"]
): string {
  switch (value) {
    case "currentEmailApprovalPending":
      return "現在の契約メールでの承認待ち";
    case "newEmailVerificationPending":
      return "新しいメールでの確認待ち";
    default:
      return "変更手続き中";
  }
}

export function AdminContractPage() {
  const env = getEnv();
  const navigate = useNavigate();
  const { tenantSlug = "unknown-tenant" } = useParams();
  const { routeStatus, sessionStatus, session, errorMessage, isSessionReady } = useProtectedRouteSession(tenantSlug);
  const [contract, setContract] = useState<AdminCommercialContractResponseDto | null>(null);
  const [loadState, setLoadState] = useState<"idle" | "loading" | "error">("idle");
  const [submitState, setSubmitState] = useState<"idle" | "submitting">("idle");
  const [newEmail, setNewEmail] = useState("");
  const [message, setMessage] = useState<string | null>(null);
  const loadInFlightRef = useRef(false);
  const hasAdminRole = Boolean(session?.member?.roles.includes("admin"));

  const loadContract = useCallback(async () => {
    if (loadInFlightRef.current) {
      return;
    }
    loadInFlightRef.current = true;
    setLoadState("loading");
    try {
      const nextContract = await fetchAdminCommercialContract(tenantSlug);
      setContract(nextContract);
      setLoadState("idle");
    } catch (error) {
      if (isUnauthenticatedApiError(error)) {
        navigate(`/t/${tenantSlug}/entry`, { replace: true });
        return;
      }
      setLoadState("error");
      setMessage(getProtectedApiErrorMessage(error, "契約情報の取得に失敗しました"));
    } finally {
      loadInFlightRef.current = false;
    }
  }, [navigate, tenantSlug]);

  useEffect(() => {
    if (!isSessionReady || !hasAdminRole) {
      return;
    }

    void loadContract();
  }, [hasAdminRole, isSessionReady, loadContract]);

  useEffect(() => {
    if (!isSessionReady || !hasAdminRole) {
      return;
    }

    function handleFocus() {
      void loadContract();
    }

    function handleVisibilityChange() {
      if (document.visibilityState === "visible") {
        void loadContract();
      }
    }

    window.addEventListener("focus", handleFocus);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      window.removeEventListener("focus", handleFocus);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [hasAdminRole, isSessionReady, loadContract]);

  useEffect(() => {
    if (
      !isSessionReady ||
      !hasAdminRole ||
      !(
        contract?.pendingContractEmailChange ||
        contract?.pendingContractCancellation ||
        contract?.pendingCancellationRevocation
      )
    ) {
      return;
    }

    const timer = window.setInterval(() => {
      void loadContract();
    }, 12_000);

    return () => {
      window.clearInterval(timer);
    };
  }, [
    contract?.pendingCancellationRevocation,
    contract?.pendingContractCancellation,
    contract?.pendingContractEmailChange,
    hasAdminRole,
    isSessionReady,
    loadContract
  ]);

  async function handleEmailChangeSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!contract?.contractContact.version) {
      setMessage("契約連絡先メールが未設定のため、変更を開始できません。");
      return;
    }

    setSubmitState("submitting");
    setMessage(null);

    try {
      const response = await requestAdminContractEmailChange(tenantSlug, {
        newEmail: newEmail.trim(),
        expectedContractContactVersion: contract.contractContact.version
      });
      setMessage(
        `変更承認メールを現在の契約連絡先へ送信する準備をしました。新しいメール: ${response.newEmailMasked}`
      );
      setNewEmail("");
      await loadContract();
    } catch (error) {
      if (isUnauthenticatedApiError(error)) {
        navigate(`/t/${tenantSlug}/entry`, { replace: true });
        return;
      }
      setMessage(getProtectedApiErrorMessage(error, "契約メール変更の開始に失敗しました"));
    } finally {
      setSubmitState("idle");
    }
  }

  const payment = contract?.platformBillingPaymentProfile;
  const paymentCard =
    payment?.brand && payment.last4
      ? `${payment.brand.toUpperCase()} **** ${payment.last4} / ${valueOrDash(payment.expiryMonth)}/${valueOrDash(payment.expiryYear)}`
      : "カード情報は未登録です";
  const effectiveEnded = session?.commercialAccess.accessMode === "contractOnly"
    && (
      session.commercialAccess.effectiveContractStatus === "ended"
      || contract?.contractStatus === "ended"
    );
  const persistedEnded = contract?.contractStatus === "ended";

  return (
    <OperatorPageScaffold
      currentArea="admin"
      tenantSlug={tenantSlug}
      tenantName={session?.tenant.displayName}
      session={session}
      title="契約・解約"
      description="契約状況、契約連絡先、料金方式、支払カード、解約手続きの状態を確認します。"
      links={pageLinks}
    >
      <div style={{ display: "grid", gap: 16 }}>
        {routeStatus === "checking" || sessionStatus === "loading" ? <p style={{ margin: 0 }}>セッションを確認しています。</p> : null}
        {routeStatus === "redirecting" ? <p style={{ margin: 0 }}>利用開始へ移動しています。</p> : null}
        {sessionStatus === "error" ? <p style={{ margin: 0 }}>セッション確認に失敗しました。{errorMessage}</p> : null}
        {routeStatus === "authenticated" && !hasAdminRole ? <p style={{ margin: 0 }}>この画面は管理者のみ利用できます。</p> : null}
        {loadState === "loading" ? <p style={{ margin: 0 }}>契約情報を読み込んでいます。</p> : null}
        {message ? <p style={{ margin: 0 }}>{message}</p> : null}
        {isSessionReady && hasAdminRole ? (
          <div>
            <button type="button" onClick={() => void loadContract()} disabled={loadState === "loading"}>
              最新の状態を確認
            </button>
          </div>
        ) : null}

        {isSessionReady && hasAdminRole && contract ? (
          <>
            <section style={{ ...cardStyle, background: contract.contractStatus ? "#f8fafc" : "#fff7ed" }}>
              <h2 style={{ margin: 0, fontSize: 18 }}>契約状況</h2>
              <p style={{ margin: 0, fontSize: 22, fontWeight: 700 }}>{describeContractStatus(contract.contractStatus)}</p>
              <dl style={{ margin: 0, display: "grid", gap: 8 }}>
                <div><dt>サービス制限</dt><dd>{describeRestrictionStatus(contract.serviceRestrictionStatus)}</dd></div>
                <div><dt>無料期間終了</dt><dd>{formatDate(contract.freeBillingEndsAt)}</dd></div>
                <div><dt>初回有料月</dt><dd>{valueOrDash(contract.firstPaidServiceMonth)}</dd></div>
                <div><dt>通常請求日</dt><dd>{valueOrDash(contract.regularChargeDayLabel)}</dd></div>
                <div><dt>商用ポリシー版</dt><dd>{valueOrDash(contract.commercialPolicyVersion)}</dd></div>
              </dl>
              {!contract.contractStatus ? (
                <p style={mutedStyle}>商用契約情報がまだ初期化されていません。運営側の初期設定後に表示されます。</p>
              ) : null}
              {effectiveEnded ? (
                <div style={{ display: "grid", gap: 6, padding: 12, borderRadius: 12, background: "#fff7ed" }}>
                  <p style={{ margin: 0, fontWeight: 700 }}>契約終了</p>
                  <p style={mutedStyle}>利用終了日: {formatDate(contract.serviceEndsAt ?? null)}</p>
                  {persistedEnded && contract.endedAt ? <p style={mutedStyle}>システム終了処理日時: {formatDate(contract.endedAt)}</p> : <p style={mutedStyle}>システム上の契約終了処理を行っています。</p>}
                  <p style={mutedStyle}>最終利用月: {contract.lastServiceMonth ?? "-"}</p>
                  <p style={mutedStyle}>最初に利用しない月: {contract.firstSkippedServiceMonth ?? "-"}</p>
                  <p style={mutedStyle}>通常のクラブ管理機能は利用できません。契約情報の確認、契約連絡先メールの変更、利用規約・プライバシーポリシーの確認、サポートへの連絡が可能です。</p>
                  <p style={mutedStyle}>未払い、返金、チャージバックその他の精算がある場合は、別途ご案内します。</p>
                  <a href={`/t/${tenantSlug}/support`}>サポート</a>
                </div>
              ) : null}
            </section>

            <section style={cardStyle}>
              <h2 style={{ margin: 0, fontSize: 18 }}>契約連絡先メール</h2>
              <p style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>
                {valueOrDash(contract.contractContact.email ?? contract.contractContact.maskedEmail)}
              </p>
              <p style={mutedStyle}>
                状態: {describeContactStatus(contract.contractContact.status)} / 確認日時:{" "}
                {formatDate(contract.contractContact.verifiedAt)} / version: {valueOrDash(contract.contractContact.version)}
              </p>
              {contract.pendingContractEmailChange ? (
                <div style={{ padding: 12, borderRadius: 12, background: "#fef3c7" }}>
                  <p style={{ margin: 0, fontWeight: 700 }}>変更手続き中</p>
                  <p style={{ margin: "4px 0 0" }}>
                    {describePendingStage(contract.pendingContractEmailChange.status)} / 新しいメール:{" "}
                    {contract.pendingContractEmailChange.newEmailMasked} / 期限:{" "}
                    {formatDate(contract.pendingContractEmailChange.expiresAt)}
                  </p>
                </div>
              ) : null}
              <form onSubmit={(event) => void handleEmailChangeSubmit(event)} style={{ display: "grid", gap: 8 }}>
                <label style={{ display: "grid", gap: 6 }}>
                  <span style={{ fontWeight: 600 }}>新しい契約連絡先メール</span>
                  <input
                    type="email"
                    value={newEmail}
                    onChange={(event) => setNewEmail(event.target.value)}
                    disabled={!contract.capabilities.canRequestContractEmailChange || submitState === "submitting"}
                    placeholder="new-contract@example.com"
                    style={{ padding: 10, borderRadius: 8, border: "1px solid #d1d5db" }}
                  />
                </label>
                <button
                  type="submit"
                  disabled={!contract.capabilities.canRequestContractEmailChange || submitState === "submitting"}
                >
                  {submitState === "submitting" ? "送信準備中..." : "メール変更を開始"}
                </button>
                <p style={mutedStyle}>
                  現在の契約メールで承認後、新しいメールの確認リンクを送信します。リンクを開くだけでは変更されず、確認ボタン押下時に反映されます。
                </p>
                {!contract.capabilities.canRequestContractEmailChange ? (
                  <p style={mutedStyle}>契約メール変更は現在利用できません。商用契約の初期設定または運営側 gate を確認してください。</p>
                ) : null}
              </form>
            </section>

            {!effectiveEnded ? <section style={cardStyle}>
              <h2 style={{ margin: 0, fontSize: 18 }}>解約予約</h2>
              {contract.scheduledCancellation ? (
                <div style={{ display: "grid", gap: 6 }}>
                  <p style={{ margin: 0, fontWeight: 700 }}>解約予約済み</p>
                  <p style={mutedStyle}>利用終了日: {formatDate(contract.scheduledCancellation.serviceEndsAt)}</p>
                  <p style={mutedStyle}>最終利用月: {contract.scheduledCancellation.lastServiceMonth} / 最初に利用しない月: {contract.scheduledCancellation.firstSkippedServiceMonth}</p>
                  <p style={mutedStyle}>取消可能期限: {formatDate(contract.scheduledCancellation.cancellationRevocableUntil)}</p>
                  {contract.pendingCancellationRevocation ? (
                    <div style={{ padding: 12, borderRadius: 12, background: "#fef3c7" }}>
                      <p style={{ margin: 0, fontWeight: 700 }}>解約予約取消し確認待ち</p>
                      <p style={{ ...mutedStyle, marginTop: 4 }}>
                        契約連絡先メールへ確認リンクを送信しました。メールで確認されるまで、現在の解約予約は有効です。
                      </p>
                      <p style={{ ...mutedStyle, marginTop: 4 }}>
                        確認期限: {formatDate(contract.pendingCancellationRevocation.confirmationExpiresAt)}
                      </p>
                    </div>
                  ) : contract.capabilities.canRequestCancellationRevocation ? (
                    <button type="button" onClick={() => navigate(`/t/${tenantSlug}/teacher/admin/contract/cancellation/${contract.scheduledCancellation?.requestId}/revoke`)}>解約予約を取り消す</button>
                  ) : contract.cancellationRevocationUnavailableReason === "windowClosed" ? (
                    <p style={mutedStyle}>解約予約取消期限を過ぎています。継続を希望する場合はサポートへお問い合わせください。</p>
                  ) : (
                    <p style={mutedStyle}>解約予約の取消しは現在利用できません。</p>
                  )}
                </div>
              ) : contract.pendingContractCancellation ? (
                <div style={{ display: "grid", gap: 6 }}>
                  <p style={{ margin: 0, fontWeight: 700 }}>解約確認待ち</p>
                  <p style={mutedStyle}>契約連絡先メールへ確認リンクを送信しました。メールで確定されるまで解約予約は成立しません。</p>
                  <p style={mutedStyle}>予定利用終了日: {formatDate(contract.pendingContractCancellation.serviceEndsAt)} / 確認期限: {formatDate(contract.pendingContractCancellation.confirmationExpiresAt)}</p>
                </div>
              ) : (
                <div style={{ display: "grid", gap: 8 }}>
                  <p style={{ margin: 0 }}>現在予約はありません。</p>
                  <p style={mutedStyle}>本日手続きを開始した場合の利用終了予定日は、次のプレビュー画面で確認できます。</p>
                  <button type="button" disabled={!contract.capabilities.canPreviewCancellation} onClick={() => navigate(`/t/${tenantSlug}/teacher/admin/contract/cancellation`)}>解約手続きへ進む</button>
                  {!contract.capabilities.canPreviewCancellation ? <p style={mutedStyle}>解約予約は現在利用できません。契約状態または確認待ちの手続きを確認してください。</p> : null}
                </div>
              )}
            </section> : null}

            {!effectiveEnded ? <section style={cardStyle}>
              <h2 style={{ margin: 0, fontSize: 18 }}>クラブ料金支払い</h2>
              <p style={{ margin: 0 }}>{describePaymentStatus(payment?.status ?? "missing")} / {paymentCard}</p>
              <button type="button" disabled>カードを変更（準備中）</button>
              <p style={mutedStyle}>Stripe portal 連携後に利用できます。provider ID は画面に表示しません。</p>
            </section> : null}

            {!effectiveEnded ? <section style={cardStyle}>
              <h2 style={{ margin: 0, fontSize: 18 }}>料金方式</h2>
              <p style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>{describePricingMode(contract.contractPricingMode)}</p>
              <button type="button" disabled>料金方式の変更を相談（準備中）</button>
              <p style={mutedStyle}>料金方式の変更は運営確認と契約連絡先メールでの同意後に扱います。</p>
            </section> : null}

            <section style={cardStyle}>
              <h2 style={{ margin: 0, fontSize: 18 }}>規約・ポリシー</h2>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                {env.legalTermsUrl ? <a href={env.legalTermsUrl} target="_blank" rel="noopener noreferrer">利用規約</a> : <span>利用規約: 準備中</span>}
                {env.legalPrivacyUrl ? <a href={env.legalPrivacyUrl} target="_blank" rel="noopener noreferrer">プライバシーポリシー</a> : <span>プライバシーポリシー: 準備中</span>}
              </div>
            </section>
          </>
        ) : null}
      </div>
    </OperatorPageScaffold>
  );
}
